import { useState, useMemo } from 'react';
import {
  ToggleLeft,
  SlidersHorizontal,
  Search,
  Beaker,
  ShieldAlert,
  Copy,
  Loader2,
  BookTemplate,
  Filter,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useCriteriaTemplates, useCloneTemplate } from '@/features/criteria/hooks/useCriteria';
import { toast } from 'sonner';
import type { Criterion, CriterionType, TemplateCategory } from '@/features/criteria/types';
import { CRITERION_TYPE_LABELS } from '@/features/criteria/types';

// =========================================
// Props
// =========================================

interface TemplateListProps {
  onCloned?: (criterion: Criterion) => void;
}

// =========================================
// Constants
// =========================================

const TYPE_ICONS: Record<CriterionType, typeof ToggleLeft> = {
  boolean: ToggleLeft,
  scale: SlidersHorizontal,
  text_match: Search,
  pico: Beaker,
  bias_risk: ShieldAlert,
};

const TYPE_ICON_BG: Record<CriterionType, string> = {
  boolean: 'bg-indigo-100 text-indigo-600',
  scale: 'bg-emerald-100 text-emerald-600',
  text_match: 'bg-amber-100 text-amber-600',
  pico: 'bg-blue-100 text-blue-600',
  bias_risk: 'bg-rose-100 text-rose-600',
};

const TYPE_BADGE_COLORS: Record<CriterionType, string> = {
  boolean: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  scale: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  text_match: 'bg-amber-50 text-amber-700 border-amber-200',
  pico: 'bg-blue-50 text-blue-700 border-blue-200',
  bias_risk: 'bg-rose-50 text-rose-700 border-rose-200',
};

const CATEGORY_COLORS: Record<string, string> = {
  Quality: 'bg-violet-50 text-violet-700 hover:bg-violet-100',
  Methodology: 'bg-cyan-50 text-cyan-700 hover:bg-cyan-100',
  Relevance: 'bg-green-50 text-green-700 hover:bg-green-100',
  Framework: 'bg-blue-50 text-blue-700 hover:bg-blue-100',
  Bias: 'bg-rose-50 text-rose-700 hover:bg-rose-100',
  'Study Design': 'bg-amber-50 text-amber-700 hover:bg-amber-100',
  JBI: 'bg-purple-50 text-purple-700 hover:bg-purple-100',
  CASP: 'bg-orange-50 text-orange-700 hover:bg-orange-100',
  Cochrane: 'bg-rose-50 text-rose-700 hover:bg-rose-100',
};

const ALL_CATEGORIES: TemplateCategory[] = [
  'Quality',
  'Methodology',
  'Relevance',
  'Framework',
  'Bias',
  'Study Design',
  'JBI',
  'CASP',
];

// =========================================
// Component
// =========================================

export function TemplateList({ onCloned }: TemplateListProps) {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [cloningId, setCloningId] = useState<string | null>(null);

  const { data: templates, isLoading } = useCriteriaTemplates();
  const cloneTemplate = useCloneTemplate();

  // Group templates by category
  const grouped = useMemo(() => {
    if (!templates) return new Map<string, Criterion[]>();
    const map = new Map<string, Criterion[]>();
    for (const t of templates) {
      const cat = t.category ?? 'Uncategorized';
      const list = map.get(cat) ?? [];
      list.push(t);
      map.set(cat, list);
    }
    return map;
  }, [templates]);

  // Available categories (only ones that have templates)
  const availableCategories = useMemo(
    () => ALL_CATEGORIES.filter((c) => grouped.has(c)),
    [grouped]
  );

  // Filtered templates
  const filteredTemplates = useMemo(() => {
    if (!templates) return [];
    if (!selectedCategory) return templates;
    return templates.filter((t) => t.category === selectedCategory);
  }, [templates, selectedCategory]);

  const handleClone = async (templateId: string) => {
    setCloningId(templateId);
    try {
      const result = await cloneTemplate.mutateAsync(templateId);
      toast.success('Template added to your criteria');
      onCloned?.(result);
    } catch {
      toast.error('Failed to clone template');
    } finally {
      setCloningId(null);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mb-2" />
        <p className="text-sm">Loading templates...</p>
      </div>
    );
  }

  // Empty state
  if (!templates || templates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <BookTemplate className="h-10 w-10 mb-3 opacity-40" />
        <p className="text-sm font-medium">No templates available</p>
        <p className="text-xs mt-1">
          Templates will appear here once configured by an administrator.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Category filter pills */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-muted-foreground shrink-0" />
        <button
          type="button"
          onClick={() => setSelectedCategory(null)}
          className={cn(
            'inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-colors border',
            !selectedCategory
              ? 'bg-slate-900 text-white border-slate-900'
              : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
          )}
        >
          All ({templates.length})
        </button>
        {availableCategories.map((cat) => {
          const count = grouped.get(cat)?.length ?? 0;
          return (
            <button
              key={cat}
              type="button"
              onClick={() =>
                setSelectedCategory(selectedCategory === cat ? null : cat)
              }
              className={cn(
                'inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-colors border',
                selectedCategory === cat
                  ? 'bg-slate-900 text-white border-slate-900'
                  : cn('border-transparent', CATEGORY_COLORS[cat] ?? 'bg-slate-50 text-slate-600')
              )}
            >
              {cat} ({count})
            </button>
          );
        })}
      </div>

      {/* Template grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filteredTemplates.map((template) => {
          const Icon = TYPE_ICONS[template.type];
          const isCloning = cloningId === template.id;

          return (
            <Card
              key={template.id}
              className="group transition-shadow hover:shadow-md"
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  {/* Type icon */}
                  <div
                    className={cn(
                      'rounded-lg p-2 shrink-0',
                      TYPE_ICON_BG[template.type]
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-medium text-sm text-foreground truncate">
                        {template.name}
                      </h4>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            disabled={isCloning}
                            onClick={() => handleClone(template.id)}
                          >
                            {isCloning ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Copy className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Use this template</TooltipContent>
                      </Tooltip>
                    </div>

                    {template.description && (
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {template.description}
                      </p>
                    )}

                    {/* Badges */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge
                        variant="outline"
                        className={cn(
                          'text-[10px]',
                          TYPE_BADGE_COLORS[template.type]
                        )}
                      >
                        {CRITERION_TYPE_LABELS[template.type]}
                      </Badge>
                      {template.category && (
                        <Badge variant="secondary" className="text-[10px]">
                          {template.category}
                        </Badge>
                      )}
                      <span className="text-[10px] text-muted-foreground">
                        Weight: {template.weight}/5
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredTemplates.length === 0 && selectedCategory && (
        <div className="text-center py-8 text-muted-foreground">
          <p className="text-sm">No templates in &ldquo;{selectedCategory}&rdquo;</p>
        </div>
      )}
    </div>
  );
}
